import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { useAlbums, useUpdateAlbum, useCreateAlbum } from "../../hooks/useAlbums";
import { getAlbumImagesApiAdminAlbumsAlbumIdImagesGet } from "../../api/generated";
import type { AlbumCreate } from "../../hooks/useAlbums";
import { cancelButtonStyle } from "../../styles/buttonStyles";
import { GalleryForm } from "./GalleryForm";

interface BulkGalleryModalProps {
  open: boolean;
  onClose: () => void;
  selectedImageIds: string[];
  onSuccess?: () => void;
}

export const BulkGalleryModal: React.FC<BulkGalleryModalProps> = ({
  open,
  onClose,
  selectedImageIds,
  onSuccess,
}) => {
  const { data: albumsData, isLoading: albumsLoading } = useAlbums({
    page_size: 100,
  });
  const updateAlbum = useUpdateAlbum();
  const createAlbumMutation = useCreateAlbum();
  const [selectedGalleryId, setSelectedGalleryId] = useState<string>("");
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [galleryImageIds, setGalleryImageIds] = useState<string[]>([]);
  const [galleryFormOpen, setGalleryFormOpen] = useState(false);

  // Load gallery images when a gallery is selected
  React.useEffect(() => {
    const loadGalleryImages = async () => {
      if (!open || !selectedGalleryId) {
        setGalleryImageIds([]);
        return;
      }

      setLoadingGallery(true);
      try {
        // Use admin endpoint to fetch images (works for both public and private albums)
        const imagesResponse = await getAlbumImagesApiAdminAlbumsAlbumIdImagesGet({
          path: { album_id: selectedGalleryId },
        });

        if (imagesResponse.data) {
          const imageIds = (imagesResponse.data.items || [])
            .map((img) => img.id || img.cloudinary_public_id)
            .filter(Boolean);
          setGalleryImageIds(imageIds);
        }
      } catch (error) {
        console.error("Failed to load gallery images:", error);
        setGalleryImageIds([]);
      } finally {
        setLoadingGallery(false);
      }
    };

    loadGalleryImages();
  }, [open, selectedGalleryId]);

  const handleSubmit = async () => {
    if (!selectedGalleryId || selectedImageIds.length === 0) return;

    try {
      // Merge selected images with existing gallery images (avoid duplicates)
      const existingImageIds = galleryImageIds || [];
      const newImageIds = [
        ...existingImageIds,
        ...selectedImageIds.filter((id) => !existingImageIds.includes(id)),
      ];

      await updateAlbum.mutateAsync({
        albumId: selectedGalleryId,
        data: { image_ids: newImageIds },
      });

      onSuccess?.();
      onClose();
      setSelectedGalleryId("");
    } catch (error) {
      console.error("Failed to add images to gallery:", error);
    }
  };

  const handleClose = () => {
    if (!updateAlbum.isPending) {
      setSelectedGalleryId("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ px: 4, pt: 4, pb: 2 }}>
        Add Images to Gallery
      </DialogTitle>
      <DialogContent sx={{ px: 4, py: 2 }}>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Adding {selectedImageIds.length}{" "}
          {selectedImageIds.length === 1 ? "image" : "images"} to a gallery
        </Typography>

        <FormControl
          fullWidth
          sx={{
            mb: 2,
            "& .MuiInput-underline:before": {
              borderBottom: "none",
            },
            "& .MuiInput-underline:hover:before": {
              borderBottom: "none",
            },
            "& .MuiInput-underline:after": {
              borderBottom: "none",
            },
          }}
          variant="standard"
        >
          <InputLabel>Select Gallery</InputLabel>
          <Select
            value={selectedGalleryId}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "__create_new__") {
                setGalleryFormOpen(true);
              } else {
                setSelectedGalleryId(value);
              }
            }}
            label="Select Gallery"
            disabled={albumsLoading || updateAlbum.isPending}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            <MenuItem value="__create_new__" sx={{ fontStyle: "italic", color: "primary.main" }}>
              + Create New Gallery...
            </MenuItem>
            {albumsData?.items.map((album) => (
              <MenuItem key={album.id} value={album.id}>
                {album.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedGalleryId && loadingGallery && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading gallery images...
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 3, px: 4, pt: 3, pb: 4 }}>
        <Button
          onClick={handleClose}
          disabled={updateAlbum.isPending}
          disableRipple
          sx={cancelButtonStyle}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="text"
          disableRipple
          disabled={
            !selectedGalleryId ||
            selectedImageIds.length === 0 ||
            updateAlbum.isPending
          }
          sx={{
            textTransform: "none",
            border: "none",
            boxShadow: "none",
            px: 3,
            py: 1,
          }}
        >
          {updateAlbum.isPending ? "Adding..." : "Add to Gallery"}
        </Button>
      </DialogActions>

      {/* Gallery Form Modal */}
      <GalleryForm
        open={galleryFormOpen}
        onClose={() => setGalleryFormOpen(false)}
        onSubmit={async (data) => {
          const result = await createAlbumMutation.mutateAsync(data as AlbumCreate);
          setSelectedGalleryId(result.id);
          setGalleryFormOpen(false);
        }}
        isLoading={createAlbumMutation.isPending}
        error={createAlbumMutation.error?.message || null}
      />
    </Dialog>
  );
};
